import React, { useState, useEffect } from 'react';
import { authFetch } from '../../auth';
import './VPNDashboard.css';

const VPNDashboard = () => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => { checkStatus(); }, []);

  const checkStatus = async (retry = 0) => {
    try {
      setLoading(true);
      const res = await authFetch('/api/vpn/status');
      const data = await res.json();
      setStatus(data);
      setError('');
      if (data.active) {
        loadConfig();
        loadQR();
      }
    } catch (err) {
      if (retry < 2) {
        setTimeout(() => checkStatus(retry + 1), 2000);
        return;
      }
      setError('Failed to check VPN status');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const res = await authFetch('/api/vpn/config');
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
      }
    } catch (err) { /* ignore */ }
  };

  const loadQR = async () => {
    try {
      const res = await authFetch('/api/vpn/qrcode');
      if (res.ok) {
        const data = await res.json();
        setQrCode(data.qrcode);
      }
    } catch (err) { /* ignore */ }
  };

  const createVPN = async () => {
    setCreating(true);
    setError('');
    try {
      const res = await authFetch('/api/vpn/create', { method: 'POST' });
      const data = await res.json();
      if (data.success || data.config) {
        setConfig(data.config);
        setStatus({ active: true, ip: data.ip, peerId: data.peerId });
        loadQR();
      } else {
        setError(data.error || 'Failed to create VPN');
      }
    } catch (err) {
      setError('Failed to create VPN. Try again.');
    } finally {
      setCreating(false);
    }
  };

  const deleteVPN = async () => {
    if (!window.confirm('Disconnect VPN? You can create a new one anytime.')) return;
    try {
      const res = await authFetch('/api/vpn/delete', { method: 'DELETE' });
      if (res.ok) {
        setStatus({ active: false });
        setConfig('');
        setQrCode('');
      }
    } catch (err) {
      setError('Failed to disconnect');
    }
  };

  const copyConfig = () => {
    navigator.clipboard.writeText(config);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadConfig = () => {
    const blob = new Blob([config], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'numfree-vpn.conf';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="vpn-dashboard">
        <div className="vpn-loading">
          <div className="vpn-spinner" />
          <p>Loading VPN status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vpn-dashboard">
      <div className="vpn-header">
        <h2>VPN</h2>
        <p className="vpn-subtitle">Secure US connection for all your devices</p>
      </div>

      {error && <div className="vpn-error">{error}</div>}

      {!status?.active ? (
        <div className="vpn-setup">
          <div className="vpn-hero">
            <div className="vpn-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h3>Connect Securely from Anywhere</h3>
            <p>Route all your traffic through our US servers. Browse as if you're home, keep your data private.</p>
            <ul className="vpn-features">
              <li>US IP address for streaming and services</li>
              <li>Encrypted connection on any WiFi</li>
              <li>Works on phone, laptop, router</li>
              <li>WireGuard — fast and lightweight</li>
            </ul>
            <button className="vpn-create-btn" onClick={createVPN} disabled={creating}>
              {creating ? 'Creating VPN...' : 'Create VPN Account'}
            </button>
          </div>
        </div>
      ) : (
        <div className="vpn-active">
          <div className="vpn-status-bar">
            <span className="vpn-status-dot active" />
            <span className="vpn-status-text">VPN Active</span>
            {status.ip && <span className="vpn-ip">IP: {status.ip}</span>}
          </div>

          {/* QR Code */}
          {qrCode && (
            <div className="vpn-qr-section">
              <h3>Scan with WireGuard App</h3>
              <p>Open WireGuard on your phone, tap +, then "Create from QR code"</p>
              <img src={qrCode} alt="VPN QR Code" className="vpn-qr-image" />
            </div>
          )}

          {/* Config */}
          <div className="vpn-config-section">
            <div className="vpn-config-header">
              <h3>Configuration File</h3>
              <div className="vpn-config-actions">
                <button onClick={copyConfig} className="vpn-btn-secondary">
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button onClick={downloadConfig} className="vpn-btn-secondary">
                  Download .conf
                </button>
                <button onClick={() => setShowConfig(!showConfig)} className="vpn-btn-secondary">
                  {showConfig ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            {showConfig && (
              <pre className="vpn-config-block">{config}</pre>
            )}
          </div>

          {/* Setup Instructions */}
          <div className="vpn-instructions">
            <h3>Setup Instructions</h3>
            <ol>
              <li><strong>Phone:</strong> Install WireGuard app, scan the QR code above</li>
              <li><strong>Computer:</strong> Install WireGuard, import the .conf file</li>
              <li><strong>Router:</strong> Paste the config into your router's WireGuard settings</li>
            </ol>
          </div>

          <div className="vpn-footer-actions">
            <button onClick={deleteVPN} className="vpn-btn-danger">Disconnect VPN</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VPNDashboard;
